import { faClose, faMotorcycle } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import THEME from "../../configuration/Theme"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { CourierEntity } from "../../Objects/Courier/CourierEntity"
import { ECourierStatusType } from "../../Enum/ECourierStatusType"
import { useMemo } from "react"
import { ECourierVehicleType } from "../../Enum/ECourierVehicleType"
import { useTranslation } from "react-i18next"
import { SIZE } from "../../configuration/Sizes"

interface AssignCourierPopupProps {
  isOpen: boolean
  couriers: CourierEntity[]
  selectedCourierId: string
  allowRandomCourier: boolean
  onClose: () => void
  onSelectCourier: (courierId: string) => void
  onConfirmSelected: () => void
  onConfirmRandom: () => void
}

export const AssignCourierPopup = ({
  isOpen,
  couriers,
  selectedCourierId,
  allowRandomCourier,
  onClose,
  onSelectCourier,
  onConfirmSelected,
  onConfirmRandom,
}: AssignCourierPopupProps) => {
  const { t } = useTranslation("frontend")
  const hasOnlineCouriers = useMemo(
    () => couriers.some((courier) => courier.status === ECourierStatusType.Online),
    [couriers],
  )

  const sortedCouriers = useMemo(
    () =>
      [...couriers].sort(
        (a, b) => Number(b.status === ECourierStatusType.Online) - Number(a.status === ECourierStatusType.Online),
      ),
    [couriers],
  )

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={"Selecionar entregador parceiro"}
              icon={faMotorcycle}
              sizeProp="1x"
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.kitchenPage.assignCourierPopup.button.onClose"
                />
              }
            />

            <div className={defaultStyle.divisionBar}></div>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-left text-xs text-slate-700">
                <p className="font-bold text-red-700">Confirmação obrigatória</p>

                <p className="mt-1">
                  Ao buscar um entregador, o restaurante reconhece que a negociação e o pagamento são feitos diretamente
                  com o entregador. O sistema não participa financeiramente do repasse.
                </p>

                {!allowRandomCourier && (
                  <p className="mt-2 font-bold text-orange-700">
                    Este pedido está com frete grátis. Por isso, ele só pode ser enviado para entregadores já vinculados
                    ao restaurante.
                  </p>
                )}
              </div>
            </Animate>

            <div className="max-h-[52dvh] pt-2 text-center overflow-y-auto p-1 flex flex-col gap-1">
              {couriers.length === 0 && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <p className="text-md mt-5 font-bold text-gray-500">Nenhum entregador parceiro encontrado.</p>
                </Animate>
              )}

              {sortedCouriers.map((courier, index) => {
                const isOffline = courier.status !== ECourierStatusType.Online

                const isSelected = selectedCourierId === courier.id

                return (
                  <Animate key={courier.id} type={AnimationTypes.fadeInTraslate} duration={`${0.2 + index}s`}>
                    <div
                      className={`
                      flex items-center justify-between border-2 border-slate-200 rounded-lg p-2 bg-white
                      transition-all duration-200 hover:bg-slate-50 hover:border-orange-500
                      ${isOffline ? "opacity-70 cursor-not-allowed bg-slate-50" : "cursor-pointer"}
                      ${isSelected ? "border-orange-400 ring-2 ring-orange-500/20" : ""}
                    `}
                    >
                      <div className="flex flex-col gap-0.5">
                        <div className="font-bold text-slate-900">{courier.personEntity.name}</div>

                        <div className="text-xs text-slate-500">
                          {ECourierVehicleType[courier.vehicleType] ?? t("kitchen.vehicle")}
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5">
                        <span
                          className={`
                          text-[11px] font-bold tracking-wide
                          inline-flex items-center gap-1
                          rounded-full px-2 py-[3px]
                          ${isOffline ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}
                        `}
                        >
                          <span className="inline-flex items-center">
                            <svg viewBox="0 0 8 8" width="8" height="8" fill="currentColor">
                              <circle cx="4" cy="4" r="4" />
                            </svg>
                          </span>

                          {isOffline ? "Offline" : "Online"}
                        </span>

                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => onSelectCourier(courier.id)}
                          disabled={isOffline}
                          className="w-4 h-4 accent-orange-600 cursor-pointer disabled:cursor-not-allowed"
                          data-interaction-description-key="interactionDescriptions:pages.kitchenPage.assignCourierPopup.input.input"
                        />
                      </div>
                    </div>
                  </Animate>
                )
              })}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className="flex justify-center gap-2 items-center">
              {couriers.length > 0 && (
                <Button
                  type="primary"
                  size="medium"
                  label={"Confirmar com selecionado"}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  disabled={!selectedCourierId || !hasOnlineCouriers}
                  onClick={onConfirmSelected}
                  descriptionKey="interactionDescriptions:pages.kitchenPage.assignCourierPopup.button.confirmarComSelecionado"
                />
              )}

              {allowRandomCourier && (
                <Button
                  type="secondary"
                  size="medium"
                  label={"Buscar entregador"}
                  bgColor="#2563eb"
                  onClick={onConfirmRandom}
                  descriptionKey="interactionDescriptions:pages.kitchenPage.assignCourierPopup.button.buscarEntregador"
                />
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
